import React, { useMemo } from "react";
import { colorConstant } from "../../styles/colorConstant/colorConstant";
import "./PosCustomRadioButton.css";
import { CircularProgress } from "@mui/material";

/**
 * PosCustomRadioButton
 *
 * A customizable radio button component for POS and e-commerce UIs. Supports multiple style variants,
 * custom icons, and flexible layout options.
 *
 * @param {Object} props
 * @param {string} [props.id] - The id for the radio input.
 * @param {string} [props.name] - The name for the radio group.
 * @param {string|number} [props.value] - The value for the radio button.
 * @param {string} [props.label] - The label to display next to the radio button.
 * @param {string} [props.variant] - Style variant for the radio button.
 * @param {string} [props.width] - Width of the radio button container.
 * @param {string} [props.height] - Height of the radio button container.
 * @param {boolean} [props.defaultIcon=true] - If true, uses default radio icon.
 * @param {boolean} [props.iconOnLeft=false] - If true, shows custom icon on the left.
 * @param {boolean} [props.iconOnRight=false] - If true, shows custom icon on the right.
 * @param {string} [props.radius] - Border radius.
 * @param {boolean} [props.transparent=false] - If true, background is transparent.
 * @param {string|number} [props.fontWeight] - Font weight for the label.
 * @param {string} [props.fontSize] - Font size for the label.
 * @param {string} [props.changeVariantOnSelect] - Variant to use when selected.
 * @param {string} [props.iconColor] - Custom color for the radio icon.
 * @param {string|number} [props.selectedValue] - The currently selected value in the group.
 * @param {function} [props.onSelect] - Change handler for the radio button.
 * @returns {JSX.Element}
 *
 * @example
 * // Basic usage
 * <PosCustomRadioButton
 *   id="option1"
 *   name="group1"
 *   value="option1"
 *   label="Option 1"
 *   selectedValue={selected}
 *   onSelect={handleSelect}
 * />
 *
 * @example
 * // With all props
 * <PosCustomRadioButton
 *   id="vegan"
 *   name="diet"
 *   value="vegan"
 *   label="Vegan"
 *   variant="green-d"
 *   width={"12px"}
 *   height={"12px"}
 *   defaultIcon={false}
 *   iconOnLeft={true}
 *   incoOnRigjt={false}
 *   radius={"4px"}
 *   transparent={true}
 *   selectedValue={selected}
 *   onSelect={handleSelect}
 *   iconColor="#4caf50"
 *   fontWeight=700
 *   fontSize="1rem"
 *   changeVariantOnSelect="black-s"
 *   iconColor="blue"
 * />
 */

export const PosCustomRadioButton = ({
  id,
  name,
  value,
  label,
  variant,
  width,
  height,
  defaultIcon = true,
  iconOnLeft = false,
  iconOnRight = false,
  radius,
  transparent = false,
  selectedValue,
  onSelect,
  fontWeight,
  fontSize,
  changeVariantOnSelect,
  iconColor,
  loader,
  disabled = false,
  padding
}) => {
  //Checking for selected value
  const isSelected = selectedValue === value;

  //Changing variants to desired styles.
  const appliedVariant = useMemo(() => {
    if (variant === "white-h" && isSelected) {
      return changeVariantOnSelect;
    }
    if (variant === "black-s" && isSelected) {
      return changeVariantOnSelect;
    }
    return variant;
  }, [variant, isSelected]);

  //Creating Varaints for the Radio Button.
  const variants = useMemo(() => {
    const baseStyles =
      {
        "white-h": {
          background: `${transparent ? "transparent" : colorConstant.surfaceG0}`,
          color: colorConstant.surfaceG4,
          border: "none",
        },
        "white-d": {
          background: `${transparent ? "transparent" : colorConstant.surfaceWhite}`,
          color: colorConstant.surfaceG5,
          border: "none",
        },
        "blue-d": {
          background: colorConstant.blueB0,
          color: colorConstant.surfaceWhite,
          border: `1.5px solid ${colorConstant.blueB0}`,
        },
        "blue-s": {
          background: `${transparent ? "transparent" : colorConstant.blueB4}`,
          color: colorConstant.blueB0,
          border: `1.5px solid ${colorConstant.blueB0}`,
        },
        "black-s": {
          background: colorConstant.surfaceWhite,
          color: colorConstant.surfaceG3,
          border: `1px solid ${colorConstant.surfaceG3}`,
        },
        "green-d": {
          background: colorConstant.greenG4,
          color: colorConstant.surfaceG3,
          border: `none`,
        },
        "red-light": {
          background: colorConstant.redLight,
          color: colorConstant.surfaceG3,
          border: `none`,
        },
      }[appliedVariant] || {};

    // Apply disabled styles if disabled is true
    if (disabled) {
      return {
        ...baseStyles,
        opacity: 0.5,
        cursor: "not-allowed",
      };
    }
    return baseStyles;
  }, [appliedVariant, disabled]);

  //Checking whether to show the custom icon or not
  const showCustomIcon = !defaultIcon && (iconOnLeft || iconOnRight);

  return (
    <label
      htmlFor={id}
      className="posCustomRadioButtonWrapper"
      style={{
        ...variants,
        width: width,
        height: height,
        borderRadius: radius,
        fontWeight: fontWeight,
        fontSize: fontSize || "1rem",
        cursor: disabled ? "not-allowed" : "pointer",
        padding: padding
      }}
    >
      <input
        type="radio"
        id={id}
        name={name}
        value={value}
        checked={isSelected}
        onChange={onSelect}
        className={!defaultIcon ? "hideDefaultIcon" : ""}
        disabled={disabled}
      />
      {loader ? (
        <CircularProgress color="success" sx={{ color: "#FFFFFF" }} size={20} />
      ) : (
        // <>
        //   {showCustomIcon && (
        //     <span
        //       className={`posCustomRadioIcon ${variant ? variant : ""} ${isSelected ? "act" : ""}`}
        //       style={{
        //         borderColor: iconColor || "var(--surface-g3)",
        //       }}
        //     >
        //       {isSelected && (
        //         <span
        //           style={{
        //             width: "8px",
        //             height: "8px",
        //             backgroundColor: iconColor || "var(--blue-b0)",
        //             borderRadius: "50%",
        //             display: "block",
        //           }}
        //         />
        //       )}
        //     </span>
        //   )}
        //   <span className={`posCustomRadioButtonLabel`} style={{ opacity: disabled ? 0.5 : 1 }}>
        //     {label}
        //   </span>
        // </>
        <>
          {iconOnLeft && showCustomIcon && (
            <span
              className={`posCustomRadioIcon ${variant ? variant : ""} ${isSelected ? "act" : ""}`}
              style={{
                borderColor: iconColor || "var(--surface-g3)",
              }}
            >
              {isSelected && (
                <span
                  style={{
                    width: "8px",
                    height: "8px",
                    backgroundColor: iconColor || "var(--blue-b0)",
                    borderRadius: "50%",
                    display: "block",
                  }}
                />
              )}
            </span>
          )}

          <span className={`posCustomRadioButtonLabel`} style={{ opacity: disabled ? 0.5 : 1 }}>
            {label}
          </span>

          {iconOnRight && showCustomIcon && (
            <span
              className={`posCustomRadioIcon ${variant ? variant : ""} ${isSelected ? "act" : ""}`}
              style={{
                borderColor: iconColor || "var(--surface-g3)",
              }}
            >
              {isSelected && (
                <span
                  style={{
                    width: "8px",
                    height: "8px",
                    backgroundColor: iconColor || "var(--blue-b0)",
                    borderRadius: "50%",
                    display: "block",
                  }}
                />
              )}
            </span>
          )}
        </>
      )}
    </label>
  );
};
